import type { ComponentType, ReactNode } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { listaDesdeTexto, textoDeCampo } from '@/lib/campos/valor-de-campo';
import type {
  Currency,
  CustomFieldDef,
  CustomFields as CustomFieldValues,
  CustomFieldValue,
} from '@/types';
import { FilterSelect } from '../filter-select';
import { InlineEdit } from '../inline-edit';
import { Money } from '../money';
import { DateField, FieldRow, SelectField, ToggleField } from './field-controls';

export interface CustomFieldsSectionProps {
  fields: readonly CustomFieldDef[];
  values: CustomFieldValues;
  /** The tenant's currency: a money field of a USD client is not read in pesos. */
  currency: Currency;
  onSave: (key: string, value: CustomFieldValue) => Promise<void>;
  isPending: boolean;
  isError: boolean;
  onRetry: () => void;
  /**
   * Quien mira no puede escribir: cada valor se lee como texto y no hay ningún
   * control. No es un control deshabilitado —un selector gris invita a hacer
   * clic y no explica nada—: es el dato, y nada más.
   */
  readOnly?: boolean;
  /**
   * Un «Texto largo» se lee entero y se edita en varias líneas.
   *
   * Apagado, el texto largo usa el mismo renglón que el texto corto, que es como
   * lo dibuja la ficha de la empresa desde siempre; encenderlo allá es una
   * decisión sobre esa pantalla, no un efecto de compartir este componente.
   */
  multilineText?: boolean;
  /** Qué decir cuando la organización todavía no definió ninguno. */
  emptyText?: string;
  /**
   * El renglón etiqueta/valor de la ficha que la aloja. Cada ficha tiene el
   * suyo, y estos campos tienen que alinearse con los que tienen arriba y abajo
   * en vez de traer la grilla de otra pantalla.
   */
  Row?: ComponentType<{ label: string; children: ReactNode }>;
}

const asText = (value: CustomFieldValue | undefined): string =>
  value === null || value === undefined ? '' : String(value);

const asList = (value: CustomFieldValue | undefined): string[] =>
  Array.isArray(value) ? value : [];

/**
 * The client's own fields, rendered by the type its administrator declared.
 *
 * Vive en `components/shared` porque lo dibujan la ficha de la empresa y la del
 * negocio: nació en Empresas y se mudó el día que Negocios lo necesitó, sin
 * cambiarle a la empresa nada de lo que ya hacía.
 */
export function CustomFieldsSection({
  fields,
  values,
  currency,
  onSave,
  isPending,
  isError,
  onRetry,
  readOnly = false,
  multilineText = false,
  emptyText = 'Tu organización todavía no define campos propios. Se crean en Configuración.',
  Row = FieldRow,
}: CustomFieldsSectionProps) {
  if (isPending) {
    return (
      <div className="space-y-2" aria-busy>
        {[0, 1, 2].map((row) => (
          <Skeleton key={row} className="h-8 w-full" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div role="alert" className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">No pudimos cargar los campos del cliente.</p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          <RefreshCw className="size-3.5" />
          Reintentar
        </Button>
      </div>
    );
  }

  if (fields.length === 0) {
    return <p className="text-sm text-muted-foreground">{emptyText}</p>;
  }

  return (
    <div className="space-y-0.5">
      {fields.map((field) => (
        <Row key={field.id} label={field.label}>
          {readOnly ? (
            <ReadOnlyValue text={textoDeCampo(field, values[field.key], currency)} />
          ) : (
            <CustomFieldControl
              field={field}
              value={values[field.key]}
              currency={currency}
              multilineText={multilineText}
              onSave={onSave}
            />
          )}
        </Row>
      ))}
    </div>
  );
}

/** El valor sin control: mismo renglón y mismo margen que el campo editable. */
function ReadOnlyValue({ text }: { text: string }) {
  return (
    <p className="whitespace-pre-wrap break-words px-1.5 py-1 text-sm">
      {text === '' ? <span className="text-muted-foreground">Sin valor</span> : text}
    </p>
  );
}

interface ControlProps {
  field: CustomFieldDef;
  value: CustomFieldValue | undefined;
  currency: Currency;
  multilineText: boolean;
  onSave: (key: string, value: CustomFieldValue) => Promise<void>;
}

function CustomFieldControl({ field, value, currency, multilineText, onSave }: ControlProps) {
  const save = (next: CustomFieldValue) => onSave(field.key, next);
  const options = (field.options ?? []).map((option) => ({ value: option, label: option }));

  switch (field.type) {
    case 'boolean':
      return <ToggleField label={field.label} value={value === true} onChange={save} />;

    case 'select':
      return (
        <SelectField
          label={field.label}
          value={typeof value === 'string' ? value : null}
          options={options}
          onChange={save}
        />
      );

    case 'multiselect':
      return (
        <FilterSelect
          label={field.label}
          options={options}
          value={asList(value)}
          onChange={(next) => void save(next)}
        />
      );

    case 'textlist':
      return (
        <InlineEdit
          label={field.label}
          value={asList(value).join(', ')}
          placeholder={field.required ? 'Obligatorio' : 'Sin valor'}
          validate={(next) =>
            field.required && listaDesdeTexto(next).length === 0 ? 'Este campo es obligatorio.' : null
          }
          onSave={(next) => {
            const lista = listaDesdeTexto(next);
            return save(lista.length > 0 ? lista : null);
          }}
        />
      );

    case 'date':
      return (
        <DateField
          label={field.label}
          value={typeof value === 'string' && value ? value : null}
          onChange={save}
        />
      );

    case 'currency':
      return (
        <InlineEdit
          label={field.label}
          value={asText(value)}
          placeholder="Sin monto"
          validate={(next) => (next === '' || Number.isFinite(Number(next)) ? null : 'Escribe un número.')}
          render={(shown) => <Money value={Number(shown)} currency={currency} />}
          onSave={(next) => save(next === '' ? null : Number(next))}
        />
      );

    case 'number':
      return (
        <InlineEdit
          label={field.label}
          value={asText(value)}
          placeholder="Sin valor"
          validate={(next) => (next === '' || Number.isFinite(Number(next)) ? null : 'Escribe un número.')}
          onSave={(next) => save(next === '' ? null : Number(next))}
        />
      );

    default: {
      // El texto largo, cuando la ficha lo pide entero: se edita en un área de
      // varias líneas y se lee sin cortar. `InlineEdit` trunca lo que muestra a
      // un renglón; el `render` devuelve el texto con sus saltos adentro de ese
      // renglón, que crece con él.
      const largo = multilineText && field.type === 'textarea';
      return (
        <InlineEdit
          label={field.label}
          value={asText(value)}
          placeholder={field.required ? 'Obligatorio' : 'Sin valor'}
          {...(largo
            ? {
                multiline: true,
                render: (shown: string) => (
                  <span className="block whitespace-pre-wrap break-words">{shown}</span>
                ),
              }
            : {})}
          validate={(next) =>
            field.required && next === '' ? 'Este campo es obligatorio.' : null
          }
          onSave={(next) => save(next === '' ? null : next)}
        />
      );
    }
  }
}
